import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const MarcarAsistenciaActividad = () => {
  const { actividadId } = useParams();
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);
  const [status, setStatus] = useState('loading'); // loading, success, error, already_marked, not_found, not_logged_in
  const [actividad, setActividad] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!user) {
      setStatus('not_logged_in');
      return;
    }
    
    marcarAsistencia();
  }, [user, actividadId]);

  const marcarAsistencia = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setStatus('not_logged_in');
        return;
      }

      // Intentar marcar asistencia
      const response = await axios.post(
        `${API}/actividades-extracurriculares/${actividadId}/marcar-asistencia-qr`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setActividad({ titulo: response.data.actividad_titulo });
        setStatus('success');
      }
    } catch (error) {
      console.error('Error marcando asistencia:', error);
      
      if (error.response?.status === 401) {
        setStatus('not_logged_in');
      } else if (error.response?.status === 404) {
        setStatus('not_found');
        setErrorMessage('La actividad no existe o fue eliminada');
      } else if (error.response?.status === 400) {
        const detail = error.response?.data?.detail || '';
        if (detail.includes('Ya marcaste')) {
          setStatus('already_marked');
        } else if (detail.includes('no está activa')) {
          setStatus('error');
          setErrorMessage('La actividad ya no está activa');
        } else {
          setStatus('error');
          setErrorMessage(detail);
        }
      } else {
        setStatus('error');
        setErrorMessage('Ocurrió un error al marcar la asistencia. Intenta de nuevo.');
      }
    }
  };

  const handleLogin = () => {
    // Guardar la URL actual para redirigir después del login
    localStorage.setItem('redirect_after_login', `/asistencia-actividad/${actividadId}`);
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-2xl border-0">
        <CardContent className="p-8 text-center">
          {/* Loading */}
          {status === 'loading' && (
            <div className="py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <i className="fas fa-spinner fa-spin text-emerald-600 text-2xl"></i>
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Registrando asistencia...</h2>
              <p className="text-gray-600">Por favor espera un momento</p>
            </div>
          )}

          {/* Success */}
          {status === 'success' && (
            <div className="py-8">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-check-circle text-emerald-600 text-4xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-emerald-700 mb-2">¡Asistencia Registrada!</h2>
              <p className="text-gray-600 mb-4">
                Tu asistencia a <strong>{actividad?.titulo || 'la actividad'}</strong> ha sido registrada exitosamente.
              </p>
              <div className="bg-emerald-50 p-4 rounded-lg mb-6">
                <p className="text-sm text-emerald-700">
                  <i className="fas fa-user mr-2"></i>
                  {user?.nombre} {user?.apellido}
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  <i className="fas fa-clock mr-2"></i>
                  {format(new Date(), "EEEE d 'de' MMMM, HH:mm", { locale: es })}
                </p>
              </div>
              <Button 
                onClick={() => navigate('/app/actividades-extra')}
                className="bg-emerald-600 hover:bg-emerald-700 w-full"
              >
                <i className="fas fa-star mr-2"></i>
                Ver Actividades
              </Button>
            </div>
          )}

          {/* Already Marked */}
          {status === 'already_marked' && (
            <div className="py-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-info-circle text-blue-600 text-4xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-blue-700 mb-2">Ya registraste asistencia</h2>
              <p className="text-gray-600 mb-6">
                Tu asistencia a esta actividad ya fue registrada anteriormente.
              </p>
              <Button 
                onClick={() => navigate('/app/actividades-extra')}
                className="bg-blue-600 hover:bg-blue-700 w-full"
              >
                <i className="fas fa-star mr-2"></i>
                Ver Actividades
              </Button>
            </div>
          )}

          {/* Not Found */}
          {status === 'not_found' && (
            <div className="py-8">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-question-circle text-gray-500 text-4xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-700 mb-2">Actividad no encontrada</h2>
              <p className="text-gray-600 mb-6">
                {errorMessage || 'El código QR no corresponde a ninguna actividad válida.'}
              </p>
              <Button 
                onClick={() => navigate('/app/actividades-extra')}
                variant="outline"
                className="w-full"
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Volver a Actividades
              </Button>
            </div>
          )}

          {/* Not Logged In */}
          {status === 'not_logged_in' && (
            <div className="py-8">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-user-lock text-amber-600 text-4xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Inicia sesión</h2>
              <p className="text-gray-600 mb-6">
                Para registrar tu asistencia, primero debes iniciar sesión en tu cuenta.
              </p>
              <Button 
                onClick={handleLogin}
                className="bg-emerald-600 hover:bg-emerald-700 w-full mb-3"
              >
                <i className="fas fa-sign-in-alt mr-2"></i>
                Iniciar Sesión
              </Button>
              <p className="text-xs text-gray-500">
                Después de iniciar sesión, tu asistencia se registrará automáticamente.
              </p>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="py-8">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-exclamation-triangle text-red-600 text-4xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-red-700 mb-2">Error</h2>
              <p className="text-gray-600 mb-6">
                {errorMessage || 'Ocurrió un error al registrar tu asistencia.'}
              </p>
              <div className="space-y-2">
                <Button 
                  onClick={marcarAsistencia}
                  className="bg-emerald-600 hover:bg-emerald-700 w-full"
                >
                  <i className="fas fa-redo mr-2"></i>
                  Intentar de nuevo
                </Button>
                <Button 
                  onClick={() => navigate('/app/actividades-extra')}
                  variant="outline"
                  className="w-full"
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Volver
                </Button>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 pt-4 border-t">
            <div className="flex items-center justify-center text-gray-400 text-sm">
              <i className="fas fa-star text-emerald-400 mr-2"></i>
              IBA Connect - Actividades Extracurriculares
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MarcarAsistenciaActividad;
